import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {deleteDepotAPI, getDepotsAPI, insertDepotAPI, updateDepotAPI} from "../../api/depot";
import {
    deactivateDriverAPI,
    deleteDriverAbsenceAPI,
    getAbsencesInPeriodAPI,
    getDriverAbsencesAPI,
    getDriverByIdAPI,
    getDriversAPI,
    insertDriverAbsenceAPI,
    importDriversAPI,
    insertDriverAPI,
    updateDriverAPI
} from "../../api/driver";
import {
    cancelAssignmentAPI,
    checkOrApplyAssignmentAPI,
    copyWeekAPI,
    getDispatchDeparturesAPI,
    getDispatchScheduleAPI,
    getUnassignedDeparturesAPI,
    repeatAssignmentAPI,
    assignTurnusAPI,
    cancelTurnusAssignmentAPI,
    getWorkLimitsAPI,
    getWorkWarningsAPI,
    saveWorkLimitsAPI
} from "../../api/dispatch";
import {
    autogenerateTurnusAPI,
    copyTurnusAPI,
    deleteTurnusAPI,
    getTurnusByIdAPI,
    getTurnusListAPI,
    insertTurnusAPI,
    updateTurnusAPI
} from "../../api/turnus";

function serverMessage(error, fallback) {
    const data = error?.response?.data;
    if (data?.details?.length) {
        return data.details.join(" ");
    }
    return data?.message || data?.error || fallback;
}

export const fetchDepots = createAsyncThunk("dispatch/fetchDepots",
    async (onlyActive, {rejectWithValue}) => {
        try {
            const response = await getDepotsAPI(onlyActive);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri učitavanju pogona."));
        }
    });

export const saveDepot = createAsyncThunk("dispatch/saveDepot",
    async ({id, payload}, {rejectWithValue}) => {
        try {
            const response = id ? await updateDepotAPI(id, payload) : await insertDepotAPI(payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri čuvanju pogona."));
        }
    });

export const removeDepot = createAsyncThunk("dispatch/removeDepot",
    async (id, {rejectWithValue}) => {
        try {
            await deleteDepotAPI(id);
            return id;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri brisanju pogona."));
        }
    });

export const fetchDrivers = createAsyncThunk("dispatch/fetchDrivers",
    async (filters, {rejectWithValue}) => {
        try {
            const response = await getDriversAPI(filters);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri učitavanju vozača."));
        }
    });

export const fetchDriverById = createAsyncThunk("dispatch/fetchDriverById",
    async (id, {rejectWithValue}) => {
        try {
            const response = await getDriverByIdAPI(id);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri učitavanju vozača."));
        }
    });

export const saveDriver = createAsyncThunk("dispatch/saveDriver",
    async ({id, payload}, {rejectWithValue}) => {
        try {
            const response = id ? await updateDriverAPI(id, payload) : await insertDriverAPI(payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri čuvanju vozača."));
        }
    });

export const deactivateDriver = createAsyncThunk("dispatch/deactivateDriver",
    async (id, {rejectWithValue}) => {
        try {
            await deactivateDriverAPI(id);
            return id;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri gašenju vozača."));
        }
    });

export const fetchDriverAbsences = createAsyncThunk("dispatch/fetchDriverAbsences",
    async (driverId, {rejectWithValue}) => {
        try {
            const response = await getDriverAbsencesAPI(driverId);
            return {driverId, absences: response.data};
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri učitavanju odsustava."));
        }
    });

export const fetchAbsencesInPeriod = createAsyncThunk("dispatch/fetchAbsencesInPeriod",
    async (params, {rejectWithValue}) => {
        try {
            const response = await getAbsencesInPeriodAPI(params);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri učitavanju odsustava."));
        }
    });

export const addDriverAbsence = createAsyncThunk("dispatch/addDriverAbsence",
    async (payload, {rejectWithValue}) => {
        try {
            const response = await insertDriverAbsenceAPI(payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri upisu odsustva."));
        }
    });

export const removeDriverAbsence = createAsyncThunk("dispatch/removeDriverAbsence",
    async (id, {rejectWithValue}) => {
        try {
            await deleteDriverAbsenceAPI(id);
            return id;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri brisanju odsustva."));
        }
    });

export const fetchDepartures = createAsyncThunk("dispatch/fetchDepartures",
    async (params, {rejectWithValue}) => {
        try {
            const response = await getDispatchDeparturesAPI(params);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri učitavanju polazaka."));
        }
    });

export const fetchUnassigned = createAsyncThunk("dispatch/fetchUnassigned",
    async (params, {rejectWithValue}) => {
        try {
            const response = await getUnassignedDeparturesAPI(params);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri učitavanju polazaka bez vozača."));
        }
    });

export const checkOrApplyAssignment = createAsyncThunk("dispatch/checkOrApplyAssignment",
    async (payload, {rejectWithValue}) => {
        try {
            const response = await checkOrApplyAssignmentAPI(payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri dodeli vozača."));
        }
    });

export const cancelAssignment = createAsyncThunk("dispatch/cancelAssignment",
    async (id, {rejectWithValue}) => {
        try {
            await cancelAssignmentAPI(id);
            return id;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri uklanjanju dodele."));
        }
    });

export const fetchSchedule = createAsyncThunk("dispatch/fetchSchedule",
    async (params, {rejectWithValue}) => {
        try {
            const response = await getDispatchScheduleAPI(params);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri učitavanju rasporeda."));
        }
    });

export const repeatAssignment = createAsyncThunk("dispatch/repeatAssignment",
    async (payload, {rejectWithValue}) => {
        try {
            const response = await repeatAssignmentAPI(payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri ponavljanju rasporeda."));
        }
    });

export const copyWeek = createAsyncThunk("dispatch/copyWeek",
    async (payload, {rejectWithValue}) => {
        try {
            const response = await copyWeekAPI(payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri prepisivanju nedelje."));
        }
    });

export const fetchTurnusList = createAsyncThunk("dispatch/fetchTurnusList",
    async (params, {rejectWithValue}) => {
        try {
            const response = await getTurnusListAPI(params);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri učitavanju turnusa."));
        }
    });

export const fetchTurnusById = createAsyncThunk("dispatch/fetchTurnusById",
    async (id, {rejectWithValue}) => {
        try {
            const response = await getTurnusByIdAPI(id);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri učitavanju turnusa."));
        }
    });

export const saveTurnus = createAsyncThunk("dispatch/saveTurnus",
    async ({id, payload}, {rejectWithValue}) => {
        try {
            const response = id ? await updateTurnusAPI(id, payload) : await insertTurnusAPI(payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri čuvanju turnusa."));
        }
    });

export const copyTurnus = createAsyncThunk("dispatch/copyTurnus",
    async (id, {rejectWithValue}) => {
        try {
            const response = await copyTurnusAPI(id);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri kopiranju turnusa."));
        }
    });

export const removeTurnus = createAsyncThunk("dispatch/removeTurnus",
    async (id, {rejectWithValue}) => {
        try {
            await deleteTurnusAPI(id);
            return id;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri brisanju turnusa."));
        }
    });

export const autogenerateTurnus = createAsyncThunk("dispatch/autogenerateTurnus",
    async (tripIds, {rejectWithValue}) => {
        try {
            const response = await autogenerateTurnusAPI(tripIds);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri pravljenju predloga."));
        }
    });

export const assignTurnus = createAsyncThunk("dispatch/assignTurnus",
    async (payload, {rejectWithValue}) => {
        try {
            const response = await assignTurnusAPI(payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri dodeli turnusa."));
        }
    });

export const cancelTurnusAssignment = createAsyncThunk("dispatch/cancelTurnusAssignment",
    async (payload, {rejectWithValue}) => {
        try {
            const response = await cancelTurnusAssignmentAPI(payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri uklanjanju turnusa."));
        }
    });

export const fetchWorkWarnings = createAsyncThunk("dispatch/fetchWorkWarnings",
    async (params, {rejectWithValue}) => {
        try {
            const response = await getWorkWarningsAPI(params);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri proveri radnog vremena."));
        }
    });

export const fetchWorkLimits = createAsyncThunk("dispatch/fetchWorkLimits",
    async (_, {rejectWithValue}) => {
        try {
            const response = await getWorkLimitsAPI();
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri učitavanju pragova."));
        }
    });

export const saveWorkLimits = createAsyncThunk("dispatch/saveWorkLimits",
    async (payload, {rejectWithValue}) => {
        try {
            const response = await saveWorkLimitsAPI(payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri čuvanju pragova."));
        }
    });

export const importDrivers = createAsyncThunk("dispatch/importDrivers",
    async (payload, {rejectWithValue}) => {
        try {
            const response = await importDriversAPI(payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(serverMessage(error, "Greška pri uvozu vozača."));
        }
    });

const initialState = {
    depots: [],
    depotsLoading: false,
    drivers: [],
    driversLoading: false,
    absences: [],
    absencesLoading: false,
    absencesDriverId: null,
    periodAbsences: [],
    departures: [],
    departuresLoading: false,
    schedule: [],
    scheduleLoading: false,
    turnusList: [],
    turnusLoading: false,
    workWarnings: [],
    workLimits: null
};

const slice = createSlice({
    name: "dispatch",
    initialState,
    reducers: {
        clearAbsences(state) {
            state.absences = [];
            state.absencesDriverId = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDepots.pending, (state) => {
                state.depotsLoading = true;
            })
            .addCase(fetchDepots.fulfilled, (state, action) => {
                state.depotsLoading = false;
                state.depots = action.payload || [];
            })
            .addCase(fetchDepots.rejected, (state) => {
                state.depotsLoading = false;
            })
            .addCase(removeDepot.fulfilled, (state, action) => {
                state.depots = state.depots.filter((depot) => depot.id !== action.payload);
            })

            .addCase(fetchDrivers.pending, (state) => {
                state.driversLoading = true;
            })
            .addCase(fetchDrivers.fulfilled, (state, action) => {
                state.driversLoading = false;
                state.drivers = action.payload || [];
            })
            .addCase(fetchDrivers.rejected, (state) => {
                state.driversLoading = false;
            })
            .addCase(deactivateDriver.fulfilled, (state, action) => {
                const driver = state.drivers.find((row) => row.id === action.payload);
                if (driver) {
                    driver.active = false;
                }
            })

            .addCase(fetchDriverAbsences.pending, (state) => {
                state.absencesLoading = true;
            })
            .addCase(fetchDriverAbsences.fulfilled, (state, action) => {
                state.absencesLoading = false;
                state.absencesDriverId = action.payload.driverId;
                state.absences = action.payload.absences || [];
            })
            .addCase(fetchDriverAbsences.rejected, (state) => {
                state.absencesLoading = false;
            })
            .addCase(addDriverAbsence.fulfilled, (state, action) => {
                state.absences = [action.payload, ...state.absences];
            })
            .addCase(removeDriverAbsence.fulfilled, (state, action) => {
                state.absences = state.absences.filter((row) => row.id !== action.payload);
            })

            .addCase(fetchDepartures.pending, (state) => {
                state.departuresLoading = true;
            })
            .addCase(fetchDepartures.fulfilled, (state, action) => {
                state.departuresLoading = false;
                state.departures = action.payload || [];
            })
            .addCase(fetchDepartures.rejected, (state) => {
                state.departuresLoading = false;
            })
            .addCase(fetchUnassigned.pending, (state) => {
                state.departuresLoading = true;
            })
            .addCase(fetchUnassigned.fulfilled, (state, action) => {
                state.departuresLoading = false;
                state.departures = action.payload || [];
            })
            .addCase(fetchUnassigned.rejected, (state) => {
                state.departuresLoading = false;
            })

            .addCase(fetchSchedule.pending, (state) => {
                state.scheduleLoading = true;
            })
            .addCase(fetchSchedule.fulfilled, (state, action) => {
                state.scheduleLoading = false;
                state.schedule = action.payload || [];
            })
            .addCase(fetchSchedule.rejected, (state) => {
                state.scheduleLoading = false;
            })

            .addCase(fetchTurnusList.pending, (state) => {
                state.turnusLoading = true;
            })
            .addCase(fetchTurnusList.fulfilled, (state, action) => {
                state.turnusLoading = false;
                state.turnusList = action.payload || [];
            })
            .addCase(fetchTurnusList.rejected, (state) => {
                state.turnusLoading = false;
            })
            .addCase(removeTurnus.fulfilled, (state, action) => {
                state.turnusList = state.turnusList.filter((row) => row.id !== action.payload);
            })

            .addCase(fetchAbsencesInPeriod.fulfilled, (state, action) => {
                state.periodAbsences = action.payload || [];
            })
            .addCase(fetchAbsencesInPeriod.rejected, (state) => {
                state.periodAbsences = [];
            })

            .addCase(fetchWorkWarnings.fulfilled, (state, action) => {
                state.workWarnings = action.payload || [];
            })
            .addCase(fetchWorkWarnings.rejected, (state) => {
                state.workWarnings = [];
            })
            .addCase(fetchWorkLimits.fulfilled, (state, action) => {
                state.workLimits = action.payload || null;
            })
            .addCase(saveWorkLimits.fulfilled, (state, action) => {
                state.workLimits = action.payload || null;
            });
    }
});

export const {clearAbsences} = slice.actions;
export default slice.reducer;
